import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Skeleton,
  Progress,
  Typography,
  Space,
  Tag,
  message,
  Input,
  Empty,
  Result,
  Alert,
  Tour,
  Tabs,
  Divider,
  Switch,
} from 'antd';
import { v2ArticlesApi, publicSamplesApi } from '../../../../api';
import '../step1/generation.css';
import TipsRotator from '../../../Common/TipsRotator';
import { generationStep2Tips, waitingTips } from '../../../Common/tipsData';
import { useTutorialTour } from '../../../../hooks/useTutorialTour';
import { TOUR_CONFIGS } from '../../../../config/tourConfigs';
import EnhancedTour from '../../../Common/EnhancedTour';
import { CopyOutlined } from '@ant-design/icons';

const OutlineSkeletonCard = () => (
  <div className="outline-card-wrap">
    <div className="outline-card">
      <Skeleton active title={{ width: '70%' }} paragraph={{ rows: 10 }} />
    </div>
  </div>
);

const GenerationOutlinePage = ({ articleData, isGuest = false }) => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [data, setData] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const startedRef = useRef(false);
  const pollingRef = useRef(null);
  const [freeOutline, setFreeOutline] = useState('');
  const [error, setError] = useState(null);
  const errorCountRef = useRef(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const timeoutTimerRef = useRef(null);
  const [typewriterRevealed, setTypewriterRevealed] = useState(false);
  const typewriterTimersRef = useRef([]);
  const prevLoadingRef = useRef(true);
  const [expandDetails, setExpandDetails] = useState(false);
  const [highlightedRefId, setHighlightedRefId] = useState(null);
  const [thinkingText, setThinkingText] = useState('');
  const [thinkingDone, setThinkingDone] = useState(false);
  const wsRef = useRef(null);
  const thinkingEndRef = useRef(null);

  useEffect(() => {
    if (thinkingEndRef.current) {
      thinkingEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [thinkingText]);

  // 閲覧専用モード（status='completed'の場合）
  const isReadOnly = articleData?.status === 'completed';

  // ✅ ツアー管理
  const {
    tourOpen,
    completeTour,
    skipTour,
    restartTour,
    startTour,
    currentStep,
    updateCurrentStep,
    loading: tourLoading,
    isMicroMode,
  } = useTutorialTour('generation_step2', { autoStart: false, microKey: 'generation_step2_micro', disabled: isGuest });

  // 親コンポーネントからのツアー再表示イベントをリッスン
  useEffect(() => {
    const handleRestartTour = () => restartTour();
    window.addEventListener('restartGenerationTour', handleRestartTour);
    return () => window.removeEventListener('restartGenerationTour', handleRestartTour);
  }, [restartTour]);

  // ツアー開始タイミング管理用のref
  const tourStartedRef = useRef(false);

  const parseSection = useCallback(text => {
    // オブジェクトの場合は文字列に変換
    if (typeof text === 'object' && text !== null) {
      // 新フロー: {level: "H2", text: "xxx"} 形式
      if (text.level && text.text) {
        return { level: text.level, title: String(text.text || '') };
      }
      // rationale等のオブジェクトは無視（文字列化）
      text = JSON.stringify(text);
    }
    const textStr = String(text || '').trim();
    const match = /^(H[1234])\s*:\s*(.*)$/.exec(textStr);
    if (!match) return { level: 'H2', title: textStr };
    return { level: match[1], title: match[2] };
  }, []);

  useEffect(() => {
    let mounted = true;
    const articleApi = isGuest ? publicSamplesApi : v2ArticlesApi;
    const tick = async () => {
      try {
        const res = await articleApi.getArticle(uuid);
        if (!mounted) return;

        // エラーカウントをリセット（成功時）
        errorCountRef.current = 0;
        setData(res);

        const status = String(res?.status || '').toLowerCase();
        // queued中は0%、generatingで秒数ルール開始（分析なし50秒、分析あり80秒）
        if (status === 'generating' && !startedRef.current) {
          startedRef.current = true;
          const startedAt = Date.now();
          // 分析結果の有無で秒数を変える（4ステップフロー対応）
          const hasAnalysis = Boolean(res?.step1_analysis_id);
          const estimatedSeconds = hasAnalysis ? 330 : 180; // 新フロー: 約5.5分（分析あり）、約3分（分析なし）
          const timer = setInterval(() => {
            const elapsed = (Date.now() - startedAt) / 1000;
            const ratio = Math.min(elapsed / estimatedSeconds, 1);
            setProgress(12 + Math.floor(ratio * 83)); // 12→95
            if (ratio >= 1) clearInterval(timer);
          }, 200);

          // タイムアウト検知（10分 - 4ステップフロー対応・LLM応答遅延考慮）
          timeoutTimerRef.current = setTimeout(() => {
            if (!res?.outline?.outline_data?.candidates?.length) {
              setHasTimedOut(true);
              setLoading(false);
            }
          }, 600000); // 10分
        }

        // 候補到着で完了（「…よし。」表示のため少し待ってから切り替え）
        if (res?.outline?.outline_data?.candidates?.length) {
          setProgress(100);
          if (pollingRef.current) clearTimeout(pollingRef.current);
          if (timeoutTimerRef.current) clearTimeout(timeoutTimerRef.current);
          setTimeout(() => setLoading(false), 1500);
          return;
        }

        // ステータスがcompletedなのに候補がない場合は失敗として扱う
        // draftは生成開始前なので待機を続ける
        if (
          status === 'completed' &&
          !res?.outline?.outline_data?.candidates?.length
        ) {
          setLoading(false);
          if (pollingRef.current) clearTimeout(pollingRef.current);
          if (timeoutTimerRef.current) clearTimeout(timeoutTimerRef.current);
          return;
        }

        // failedステータスの場合も失敗として扱う
        if (status === 'failed') {
          setLoading(false);
          if (pollingRef.current) clearTimeout(pollingRef.current);
          if (timeoutTimerRef.current) clearTimeout(timeoutTimerRef.current);
          return;
        }
      } catch (err) {
        if (!mounted) return;

        // 404エラー: 記事が削除された（複数の形式をチェック）
        const status = err?.response?.status || err?.status;
        const statusCode = parseInt(status);

        if (statusCode === 404 || err?.message?.includes('404')) {
          setError('not_found');
          setLoading(false);
          if (pollingRef.current) clearTimeout(pollingRef.current);
          message.error('記事が見つかりません。削除された可能性があります。');
          return;
        }

        // その他のエラー: 3回連続エラーで停止
        errorCountRef.current += 1;
        if (errorCountRef.current >= 3) {
          setError('network');
          setLoading(false);
          if (pollingRef.current) clearTimeout(pollingRef.current);
          message.error(
            '記事の取得に失敗しました。ネットワーク接続を確認してください。',
          );
          return;
        }
        // 3回未満はポーリング継続
      }
      pollingRef.current = setTimeout(tick, 1200);
    };
    // 初期状態: queued想定で0%
    setProgress(0);
    tick();
    return () => {
      mounted = false;
      if (pollingRef.current) clearTimeout(pollingRef.current);
      if (timeoutTimerRef.current) clearTimeout(timeoutTimerRef.current);
    };
  }, [uuid]);

  // WebSocket接続: loading中にAI分析テキストを受信
  useEffect(() => {
    if (!loading || !uuid) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const token = localStorage.getItem('token') || '';
    let wsHost;
    if (window.location.hostname === 'localhost') {
      wsHost = 'localhost:8000';
    } else if (process.env.REACT_APP_API_URL) {
      try {
        const url = new URL(process.env.REACT_APP_API_URL);
        wsHost = url.host;
      } catch (e) {
        wsHost = window.location.host;
      }
    } else {
      wsHost = window.location.host;
    }
    const wsUrl = `${protocol}//${wsHost}/ws/v2/outline/${uuid}${token ? `?token=${token}` : ''}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onmessage = event => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'thinking_text') {
            setThinkingText(prev => prev + (msg.chunk || ''));
            if (msg.done) {
              setThinkingDone(true);
            }
          }
        } catch (e) {
          // ignore parse errors
        }
      };

      ws.onclose = () => {
        wsRef.current = null;
      };

      ws.onerror = () => {
        wsRef.current = null;
      };

      // ping keepalive
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30000);

      return () => {
        clearInterval(pingInterval);
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
          ws.close();
        }
        wsRef.current = null;
      };
    } catch (e) {
      // WebSocket connection failed, non-critical
    }
  }, [loading, uuid]);

  const candidates = useMemo(
    () => data?.outline?.outline_data?.candidates || [],
    [data],
  );

  // パターン戦略データ（タブ名用）
  const patternStrategies = useMemo(() => {
    const strategies = data?.step_states?.new_flow?.step3_patterns?.pattern_strategies || [];
    return strategies;
  }, [data]);

  // リサーチ結果（クレーム + ドメイン）
  const researchFindings = useMemo(() => {
    // step2_research.all_findingsから取得
    const step2 = data?.step_states?.new_flow?.step2_research;
    if (step2?.all_findings && Array.isArray(step2.all_findings)) {
      return step2.all_findings;
    }
    return [];
  }, [data]);
  
  // ドメイン抽出ヘルパー
  const extractDomain = (url) => {
    try {
      const u = new URL(url);
      return u.hostname.replace(/^www\./, '');
    } catch {
      return url;
    }
  };

  // 結果表示アニメーション: loading完了時に開始
  useEffect(() => {
    const wasLoading = prevLoadingRef.current;
    const isNowComplete = wasLoading && !loading && candidates.length > 0;

    prevLoadingRef.current = loading;

    if (isNowComplete) {
      setTypewriterRevealed(false);

      typewriterTimersRef.current.forEach(timer => clearTimeout(timer));
      typewriterTimersRef.current = [];

      const timer = setTimeout(() => {
        setTypewriterRevealed(true);
      }, 100);

      typewriterTimersRef.current.push(timer);
    }

    return () => {
      typewriterTimersRef.current.forEach(t => clearTimeout(t));
      typewriterTimersRef.current = [];
    };
  }, [loading, candidates.length]);

  // 情報網羅型をデフォルト選択
  useEffect(() => {
    if (!loading && candidates.length > 0 && selectedIndex === null) {
      // comprehensiveパターンを探す
      const comprehensiveIdx = candidates.findIndex(c => 
        c.pattern_id === 'comprehensive' || c.full_outline?.pattern_id === 'comprehensive'
      );
      if (comprehensiveIdx >= 0) {
        setSelectedIndex(comprehensiveIdx);
      } else {
        // 見つからない場合は最初のパターンを選択
        setSelectedIndex(0);
      }
    }
  }, [loading, candidates, selectedIndex]);

  // ✅ ツアー開始タイミング（3案生成完了時）
  useEffect(() => {
    if (
      !loading &&
      candidates.length > 0 &&
      typewriterRevealed &&
      !tourStartedRef.current &&
      !tourLoading
    ) {
      tourStartedRef.current = true;
      // DOM要素の準備を待ってからツアーを開始
      setTimeout(() => {
        startTour();
      }, 300);
    }
  }, [loading, candidates.length, typewriterRevealed, tourLoading, startTour]);

  // Tourステップ定義（micro/fullを切り替え）
  const tourSteps = useMemo(() => {
    const configKey = isMicroMode ? 'generation_step2_micro' : 'generation_step2';
    if (tourLoading || !TOUR_CONFIGS[configKey]) return [];
    return TOUR_CONFIGS[configKey].steps.map(step => {
      let targetElement;
      switch (step.target_key) {
        case 'outline_grid':
          // タブナビゲーションのみ（ボディを含まない）
          targetElement = document.querySelector('.tour-outline-grid .ant-tabs-nav');
          break;
        case 'outline_sections':
          // 左カラム（見出し構成）
          targetElement = document.querySelector('.outline-left-panel');
          break;
        case 'outline_stats':
          // 右カラム上部（構成サマリー）
          targetElement = document.querySelector('.tour-outline-stats');
          break;
        case 'outline_rationale':
          // 右カラム（構成の戦略）
          targetElement = document.querySelector('.tour-outline-rationale');
          break;
        case 'reference_list': {
          // 右カラム（参考情報リスト）
          const panels = document.querySelectorAll('.outline-right-panel > div');
          for (let panel of panels) {
            if (panel.textContent.includes('参考情報リスト')) {
              targetElement = panel;
              break;
            }
          }
          break;
        }
        case 'confirm_button':
          targetElement = document.querySelector('.tour-confirm-button button');
          break;
        // 旧キー（互換性のため）
        case 'first_card':
          targetElement = document.querySelector('.tour-first-outline-card');
          break;
        case 'card_title':
          targetElement = document.querySelector('.tour-outline-title');
          break;
        case 'card_sections':
          targetElement = document.querySelector('.tour-outline-sections');
          break;
        case 'card_stats':
          targetElement = document.querySelector('.tour-outline-stats');
          break;
        case 'card_rationale':
          targetElement = document.querySelector('.tour-outline-rationale');
          break;
        case 'selected_badge':
          targetElement = document.querySelector('.tour-selected-badge');
          break;
        default:
          targetElement = null;
      }
      return {
        ...step,
        target: targetElement ? () => targetElement : step.target || null,
      };
    });
  }, [tourLoading, typewriterRevealed, candidates.length, selectedIndex]);

  const onConfirm = useCallback(async () => {
    if (selectedIndex === null) return;
    const cand = candidates[selectedIndex];
    const selId = cand?.id || String(selectedIndex);

    try {
      // ⭐ 構造化データを準備（full_outlineがある場合はそちらを優先）
      const hierarchicalSections = [];
      const fullOutline = cand?.full_outline;
      // full_outline.outline.h2_sections または full_outline.h2_sections
      const outlineSections = fullOutline?.outline?.h2_sections || fullOutline?.h2_sections;
      console.log('[STEP2 onConfirm] expandDetails:', expandDetails, 'outlineSections:', outlineSections?.length);
      
      if (outlineSections && Array.isArray(outlineSections)) {
        // 新フロー: full_outline.outlineから構造化データを構築
        outlineSections.forEach(h2 => {
          const h2Id = Math.random().toString(36).substring(2, 10);
          const h2Section = {
            id: h2Id,
            title: h2.h2_title || h2.title || '',
            subsections: [],
          };
          
          // 展開状態ならH2のキーポイントをメモに追加
          if (expandDetails && h2.key_points?.length > 0) {
            h2Section.memo = h2.key_points.join('／');
          }
          
          const h3List = h2.h3_sections || h2.subsections || [];
          h3List.forEach(h3 => {
            const h3Id = Math.random().toString(36).substring(2, 10);
            const h3Section = {
              id: h3Id,
              title: h3.h3_title || h3.title || '',
            };
            
            // 展開状態ならH3のキーポイントもメモに追加
            if (expandDetails && h3.key_points?.length > 0) {
              h3Section.memo = h3.key_points.join('／');
            }
            
            h2Section.subsections.push(h3Section);
          });
          
          hierarchicalSections.push(h2Section);
        });
      } else {
        // 旧フロー: sectionsから解析
        const sections = cand?.sections || [];
        let currentH2 = null;

        sections.forEach(sectionStr => {
          const parsed = parseSection(sectionStr);
          const headingId = Math.random().toString(36).substring(2, 10);

          if (parsed.level === 'H2') {
            if (currentH2) {
              hierarchicalSections.push(currentH2);
            }
            currentH2 = {
              id: headingId,
              title: parsed.title,
              subsections: [],
            };
          } else if (parsed.level === 'H3' && currentH2) {
            currentH2.subsections.push({
              id: headingId,
              title: parsed.title,
            });
          }
        });

        if (currentH2) {
          hierarchicalSections.push(currentH2);
        }
      }

      // ⭐ selected_idを保存
      await v2ArticlesApi.selectOutline(uuid, selId, freeOutline || '');

      // ⭐ working_draftを保存（STEP3でIDを保持するため）
      try {
        await v2ArticlesApi.saveOutlineDraft(uuid, {
          title: cand?.title || '', // ⭐ 候補のタイトルを使用
          sections: hierarchicalSections,
        });
      } catch (draftError) {
        console.error('[Step2] Failed to save working_draft:', draftError);
        // エラーでも処理は継続（スコアリングは実行する）
      }

      message.success('ベースアウトラインを確定しました');

      // スコアリングを開始（分析結果がある場合のみ）
      if (data?.step1_analysis_id) {
        try {
          // ⭐ 既に作成済みのhierarchicalSectionsを使用
          const outlineData = {
            working_draft: {
              sections: hierarchicalSections,
            },
          };

          await v2ArticlesApi.startScoring(uuid, outlineData);

          // ⭐ スコアリング開始をマーク（STEP3でポーリングを開始するため）
          try {
            sessionStorage.setItem(
              `scoring_started_${uuid}`,
              Date.now().toString(),
            );
          } catch (_) {
            // sessionStorage使用不可の場合は無視
          }
        } catch (scoringError) {
          console.error('[Step2] Failed to start scoring:', scoringError);
          // スコアリング失敗はエラー表示しない（後で手動実行可能）
        }
      }

      // STEP3へ遷移
      navigate(`/aio/generation/${uuid}/step3`);
      window.scrollTo({ top: 0 });
    } catch (e) {
      message.error('選択の保存に失敗しました');
    }
  }, [selectedIndex, candidates, uuid, freeOutline, parseSection, expandDetails]);

  // エラー表示
  if (error === 'not_found') {
    return (
      <div className="v2-form-narrow">
        <Result
          status="404"
          title="記事が見つかりません"
          subTitle="この記事は削除されたか、アクセス権限がありません。"
          extra={
            <Button
              type="primary"
              onClick={() => navigate('/aio/generation/results')}
            >
              生成結果一覧へ戻る
            </Button>
          }
        />
      </div>
    );
  }

  if (error === 'network') {
    return (
      <div className="v2-form-narrow">
        <Result
          status="error"
          title="記事の取得に失敗しました"
          subTitle="ネットワーク接続を確認して、再度お試しください。"
          extra={
            <Space>
              <Button onClick={() => window.location.reload()}>
                再読み込み
              </Button>
              <Button
                type="primary"
                onClick={() => navigate('/aio/generation/results')}
              >
                生成結果一覧へ戻る
              </Button>
            </Space>
          }
        />
      </div>
    );
  }

  return (
    <div className="v2-form-narrow">
      {/* チュートリアルTour */}
      {!tourLoading && (
        <EnhancedTour
          open={tourOpen}
          steps={tourSteps}
          onClose={() => completeTour()}
          onFinish={() => completeTour()}
          onSkip={() => skipTour()}
          current={currentStep}
          onChange={updateCurrentStep}
          tourTitle="STEP2: アウトライン選択"
        />
      )}

      {/* TIPS（STEP2用） - 常時表示 */}
      <div style={{ marginBottom: 16 }}>
        <TipsRotator
          tips={
            loading
              ? [...generationStep2Tips, ...waitingTips]
              : generationStep2Tips
          }
          intervalMs={7000}
          fadeMs={300}
        />
      </div>

      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {loading && (
          <Progress percent={Math.min(95, progress)} status={'active'} />
        )}

        {loading && (
          <Alert
            type="info"
            showIcon
            message={<span style={{ fontSize: 13 }}>この画面を閉じても、バックグラウンドで処理が進みます。ダッシュボードで通知機能をONにすると、完了時にお知らせを受け取れます。<a href="/aio/dashboard" style={{ marginLeft: 4 }}>通知を設定する →</a></span>}
            style={{ marginBottom: 0 }}
            closable
          />
        )}

        {loading && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: 20 }}>
            {/* 左カラム: スケルトン */}
            <div style={{ background: '#fff', borderRadius: 8, padding: 20, border: '1px solid #e8e8e8' }}>
              <Skeleton active paragraph={{ rows: 8 }} />
            </div>
            {/* 右カラム: 編集者の心の声 */}
            <div style={{ position: 'relative', height: 280, borderRadius: 12, overflow: 'hidden' }}>
              {/* グラデーション背景 */}
              <div style={{
                position: 'absolute', inset: 0,
                background: 'radial-gradient(ellipse at 50% 60%, rgba(200,220,255,0.25) 0%, rgba(220,235,255,0.10) 50%, rgba(255,255,255,0) 80%)',
                pointerEvents: 'none',
              }} />
              {/* 上部フェード（テキストが消えていく） */}
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: 48, zIndex: 1,
                background: 'linear-gradient(to bottom, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)',
                pointerEvents: 'none',
              }} />
              {/* スクロール領域 */}
              <div id="thinking-scroll-area" style={{
                position: 'relative', height: '100%', overflowY: 'scroll', padding: '32px 20px 16px',
                scrollbarWidth: 'none', msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch',
              }}>
                {thinkingText ? (
                  <div style={{
                    fontSize: 13.5,
                    lineHeight: 2.0,
                    color: '#8a9bb5',
                    fontStyle: 'italic',
                    whiteSpace: 'pre-wrap',
                    letterSpacing: '0.02em',
                  }}>
                    {thinkingText}
                    {!thinkingDone && (
                      <span style={{ display: 'inline-block', width: 1.5, height: 15, background: '#b0c4de', marginLeft: 1, animation: 'blink 1s infinite', verticalAlign: 'text-bottom' }} />
                    )}
                    <div ref={thinkingEndRef} />
                  </div>
                ) : (
                  <div style={{ color: '#b0c4de', fontSize: 13, fontStyle: 'italic' }}>
                    考えています...
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        <style>{`
          @keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
          #thinking-scroll-area::-webkit-scrollbar { display: none !important; width: 0 !important; height: 0 !important; }
          #thinking-scroll-area { scrollbar-width: none !important; -ms-overflow-style: none !important; }
        `}</style>

        {!loading && candidates.length === 0 && (
          <Result
            status="warning"
            title={
              hasTimedOut
                ? '構成案の生成がタイムアウトしました'
                : '構成案の生成に失敗しました'
            }
            subTitle={
              hasTimedOut
                ? '生成に時間がかかりすぎています。再生成をお試しください。'
                : '構成案が生成されませんでした。再生成をお試しください。'
            }
            extra={
              <Button
                type="primary"
                size="large"
                loading={isRetrying}
                onClick={async () => {
                  setIsRetrying(true);
                  setHasTimedOut(false);
                  try {
                    // 記事を再取得して生成ステータスをリセット
                    await v2ArticlesApi.retryOutlineGeneration(uuid);
                    message.success('再生成を開始しました');
                    // ページをリロードして生成を再開
                    window.location.reload();
                  } catch (err) {
                    message.error('再生成の開始に失敗しました');
                    setIsRetrying(false);
                  }
                }}
              >
                構成案を再生成する
              </Button>
            }
          />
        )}

        {!loading && candidates.length > 0 && (
          <div style={{
            opacity: typewriterRevealed ? 1 : 0,
            transform: typewriterRevealed ? 'translateY(0)' : 'translateY(18px)',
            transition: 'opacity 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          }}>
            {/* タブ切り替え - 全幅 */}
            <Tabs
              activeKey={String(selectedIndex ?? 0)}
              onChange={key => setSelectedIndex(Number(key))}
              type="card"
              size="middle"
              className="tour-outline-grid outline-tabs-fullwidth"
              tabBarStyle={{ marginBottom: 0 }}
              items={candidates.map((o, idx) => {
                // パターン名を日本語に変換
                const patternId = o.pattern_id || o.full_outline?.pattern_id || '';
                const patternNameMap = {
                  'comprehensive': '情報網羅型',
                  'deep_dive': '深掘り型',
                  'creative': '共感型',
                };
                const patternName = patternNameMap[patternId] || `構成案 ${idx + 1}`;
                
                return {
                  key: String(idx),
                  label: (
                    <span style={{ 
                      padding: '4px 16px', 
                      fontSize: '13px',
                      fontWeight: selectedIndex === idx ? 600 : 400,
                    }}>
                      {patternName}
                    </span>
                  ),
                  children: (
                    <div
                      className="outline-tab-content"
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 400px',
                        gap: '20px',
                        padding: '20px 0 0 0',
                      }}
                    >
                      {/* 左カラム: 見出し一覧 */}
                      <div
                        className="outline-left-panel tour-outline-sections"
                        style={{
                          background: '#fff',
                          borderRadius: '8px',
                          padding: '16px 20px',
                          border: '1px solid #e8e8e8',
                        }}
                      >
                        {/* タイトル + 展開スイッチ */}
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'flex-start',
                          marginBottom: 12,
                        }}>
                          <h3
                            className="tour-outline-title"
                            style={{
                              fontSize: '16px',
                              fontWeight: 600,
                              margin: 0,
                              color: '#1a1a1a',
                              lineHeight: 1.5,
                              flex: 1,
                              paddingRight: 16,
                            }}
                          >
                            {o.title}
                          </h3>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                            {/* コピーボタン */}
                            <Button
                              type="text"
                              size="small"
                              icon={<CopyOutlined />}
                              onClick={() => {
                                // 構成案を[H2]/[H3]形式でコピー
                                const fullOutline = o.full_outline || {};
                                const h2Sections = fullOutline.outline?.h2_sections || [];
                                let textLines = [o.title, ''];
                                h2Sections.forEach(h2 => {
                                  textLines.push(`[H2] ${h2.h2_title}`);
                                  if (expandDetails && h2.key_points?.length > 0) {
                                    textLines.push(`      ${h2.key_points.join('／')}`);
                                  }
                                  (h2.h3_sections || []).forEach(h3 => {
                                    textLines.push(`  [H3] ${h3.h3_title}`);
                                    if (expandDetails && h3.key_points?.length > 0) {
                                      textLines.push(`        ${h3.key_points.join('／')}`);
                                    }
                                  });
                                });
                                navigator.clipboard.writeText(textLines.join('\n'));
                                message.success('構成案をコピーしました');
                              }}
                              style={{ color: '#888' }}
                            />
                            {/* 旧フローでは展開スイッチを非表示 */}
                            {o.full_outline && (
                              <>
                                <span style={{ fontSize: '11px', color: '#888' }}>展開</span>
                                <Switch 
                                  size="small" 
                                  checked={expandDetails} 
                                  onChange={setExpandDetails}
                                />
                              </>
                            )}
                          </div>
                        </div>
                        
                        <Divider style={{ margin: '0 0 12px 0' }} />
                        
                        {/* 見出しリスト */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {(() => {
                            // 新フロー: full_outlineからh2_sectionsを取得
                            const fullOutline = o.full_outline || {};
                            let h2Sections = fullOutline.outline?.h2_sections || [];
                            let isOldFlow = false;
                            
                            // 旧フロー: o.sectionsからh2/h3を解析して表示
                            if (h2Sections.length === 0 && o.sections?.length > 0) {
                              isOldFlow = true;
                              // 旧フローのsections配列を解析
                              const parsed = [];
                              let currentH2 = null;
                              (o.sections || []).forEach(s => {
                                const text = typeof s === 'string' ? s : (s?.text || s?.title || '');
                                const levelMatch = /^(H[234]):\s*(.*)$/.exec(text);
                                const level = levelMatch ? levelMatch[1] : null;
                                const title = levelMatch ? levelMatch[2] : text;
                                
                                if (level === 'H2') {
                                  currentH2 = { h2_title: title, h3_sections: [], isOldFlow: true };
                                  parsed.push(currentH2);
                                } else if ((level === 'H3' || level === 'H4') && currentH2) {
                                  currentH2.h3_sections.push({ h3_title: title, key_points: [] });
                                } else if (level === 'H3' || level === 'H4') {
                                  // H2なしでH3がある場合
                                  currentH2 = { h2_title: title, h3_sections: [], isOldFlow: true };
                                  parsed.push(currentH2);
                                } else if (!level && currentH2) {
                                  // レベルなしの項目はH3として扱う
                                  currentH2.h3_sections.push({ h3_title: title, key_points: [] });
                                } else if (!level) {
                                  // 最初の項目でレベルなし→H2として追加
                                  currentH2 = { h2_title: title, h3_sections: [], isOldFlow: true };
                                  parsed.push(currentH2);
                                }
                              });
                              h2Sections = parsed;
                            }
                            
                            return h2Sections.map((h2, h2Idx) => (
                              <div key={h2Idx}>
                                {/* H2 */}
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  paddingTop: h2Idx > 0 ? '10px' : 0,
                                }}>
                                  <Tag
                                    color="orange"
                                    style={{
                                      marginRight: 8,
                                      borderRadius: 4,
                                      minWidth: 28,
                                      textAlign: 'center',
                                      flexShrink: 0,
                                      fontSize: '12px',
                                    }}
                                  >
                                    H2
                                  </Tag>
                                  <span style={{
                                    color: '#1a1a1a',
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    lineHeight: 1.8,
                                  }}>
                                    {h2.h2_title}
                                  </span>
                                </div>
                                
                                {/* H2のキーポイント */}
                                {expandDetails && h2.key_points?.length > 0 && (
                                  <ul style={{
                                    marginLeft: 36,
                                    marginTop: 6,
                                    marginBottom: 8,
                                    paddingLeft: 16,
                                    background: '#fff8f0',
                                    borderRadius: 4,
                                    padding: '8px 12px 8px 28px',
                                    listStyle: 'disc',
                                  }}>
                                    {h2.key_points.map((kp, kpIdx) => (
                                      <li key={kpIdx} style={{ fontSize: '13px', color: '#8a6d4b', lineHeight: 1.6, marginBottom: 4 }}>
                                        {kp}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                                
                                {/* H3s */}
                                {(h2.h3_sections || []).map((h3, h3Idx) => (
                                  <div key={h3Idx}>
                                    <div style={{
                                      display: 'flex',
                                      alignItems: 'flex-start',
                                      paddingLeft: 20,
                                      marginTop: 4,
                                    }}>
                                      <Tag
                                        color="gold"
                                        style={{
                                          marginRight: 8,
                                          borderRadius: 4,
                                          minWidth: 28,
                                          textAlign: 'center',
                                          flexShrink: 0,
                                          fontSize: '12px',
                                        }}
                                      >
                                        H3
                                      </Tag>
                                      <span style={{
                                        color: '#4a4a4a',
                                        fontWeight: 400,
                                        fontSize: '14px',
                                        lineHeight: 1.8,
                                        flex: 1,
                                      }}>
                                        {h3.h3_title}
                                      </span>
                                      {/* 参照番号チップ */}
                                      {h3.research_references?.length > 0 && (
                                        <span className="heading-reference-badges" style={{ display: 'flex', gap: 3, marginLeft: 8, flexShrink: 0 }}>
                                          {h3.research_references.map((ref, refIdx) => {
                                            const refNum = parseInt(String(ref).replace(/^r/, ''), 10);
                                            return (
                                              <span
                                                key={refIdx}
                                                onClick={() => {
                                                  // 参照元にスクロール+ハイライト（タブ固有ID）
                                                  const el = document.getElementById(`ref-${idx}-${refNum}`);
                                                  if (el) {
                                                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                    setHighlightedRefId(refNum);
                                                    // 3秒後にハイライト解除
                                                    setTimeout(() => setHighlightedRefId(null), 3000);
                                                  }
                                                }}
                                                style={{
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  minWidth: 18,
                                                  height: 18,
                                                  padding: '0 5px',
                                                  fontSize: '10px',
                                                  fontWeight: 500,
                                                  color: '#666',
                                                  background: '#f0f0f0',
                                                  borderRadius: 9,
                                                  cursor: 'pointer',
                                                  transition: 'background 0.15s',
                                                }}
                                                onMouseEnter={e => { e.target.style.background = '#e0e0e0'; }}
                                                onMouseLeave={e => { e.target.style.background = '#f0f0f0'; }}
                                              >
                                                {refNum}
                                              </span>
                                            );
                                          })}
                                        </span>
                                      )}
                                    </div>
                                    
                                    {/* H3のキーポイント */}
                                    {expandDetails && h3.key_points?.length > 0 && (
                                      <ul style={{
                                        marginLeft: 56,
                                        marginTop: 4,
                                        marginBottom: 8,
                                        paddingLeft: 16,
                                        background: '#fcfaf0ff',
                                        borderRadius: 4,
                                        padding: '6px 10px 6px 24px',
                                        listStyle: 'disc',
                                      }}>
                                        {h3.key_points.map((kp, kpIdx) => (
                                          <li key={kpIdx} style={{ fontSize: '12px', color: '#8a7e4b', lineHeight: 1.5, marginBottom: 4 }}>
                                            {kp}
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ));
                          })()}
                        </div>
                      </div>

                      {/* 右カラム: 付随情報 */}
                      <div
                        className="outline-right-panel"
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '12px',
                        }}
                      >
                        {/* 統計情報 */}
                        <div
                          className="tour-outline-stats"
                          style={{
                            background: '#fafafa',
                            borderRadius: '8px',
                            padding: '16px',
                            border: '1px solid #e8e8e8',
                          }}
                        >
                          <div style={{ fontSize: '12px', color: '#666', marginBottom: '12px', fontWeight: 600 }}>
                            構成サマリー
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <div>
                              <div style={{ fontSize: '24px', fontWeight: 700, color: '#1a1a1a', lineHeight: 1 }}>
                                {(() => {
                                  const sections = o.sections || [];
                                  const h2Count = sections.filter(s => typeof s === 'string' && s.startsWith('H2:')).length;
                                  const h3Count = sections.filter(s => typeof s === 'string' && s.startsWith('H3:')).length;
                                  return `${h2Count}+${h3Count}`;
                                })()}
                              </div>
                              <div style={{ fontSize: '11px', color: '#888', marginTop: 4 }}>H2 + H3 見出し</div>
                            </div>
                            <div>
                              <div style={{ fontSize: '24px', fontWeight: 700, color: '#1a1a1a', lineHeight: 1 }}>
                                {(() => {
                                  const sections = o.sections || [];
                                  const h2Count = sections.filter(s => typeof s === 'string' && s.startsWith('H2:')).length;
                                  const h3Count = sections.filter(s => typeof s === 'string' && s.startsWith('H3:')).length;
                                  const styleData = data?.style?.style_config;
                                  let h2BodyChars = 175;
                                  let h3BodyChars = 350;
                                  if (styleData?.headings_style?.length_policy) {
                                    const policy = styleData.headings_style.length_policy;
                                    const h2Match = (policy.h2_body_chars?.typical || '').match(/(\d+)-(\d+)/);
                                    if (h2Match) h2BodyChars = Math.round((parseInt(h2Match[1]) + parseInt(h2Match[2])) / 2);
                                    const h3Match = (policy.h3_body_chars?.typical || '').match(/(\d+)-(\d+)/);
                                    if (h3Match) h3BodyChars = Math.round((parseInt(h3Match[1]) + parseInt(h3Match[2])) / 2);
                                  }
                                  const avgH3PerH2 = h2Count > 0 ? h3Count / h2Count : 0;
                                  const estimated = h2Count * (h2BodyChars + avgH3PerH2 * h3BodyChars);
                                  const rounded = Math.round(estimated / 100) * 100;
                                  return rounded >= 1000 ? `${(rounded / 1000).toFixed(1)}k` : rounded.toLocaleString();
                                })()}
                              </div>
                              <div style={{ fontSize: '11px', color: '#888', marginTop: 4 }}>想定文字数</div>
                            </div>
                          </div>
                        </div>

                        {/* 戦略・差別化 */}
                        {o.rationale && (
                          <div
                            className="tour-outline-rationale"
                            style={{
                              background: '#f8f9ff',
                              borderRadius: '8px',
                              padding: '16px',
                              border: '1px solid #e8ecff',
                            }}
                          >
                            <div style={{ fontSize: '12px', color: '#5c6bc0', marginBottom: '12px', fontWeight: 600 }}>
                              構成の戦略
                            </div>
                            {typeof o.rationale === 'string' ? (
                              <p style={{ margin: 0, fontSize: '13px', color: '#4a4a4a', lineHeight: 1.7 }}>
                                {o.rationale}
                              </p>
                            ) : (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {o.rationale.strategy_reflection && (
                                  <div>
                                    <div style={{ fontSize: '11px', color: '#7986cb', marginBottom: '4px', fontWeight: 500 }}>戦略反映</div>
                                    <p style={{ margin: 0, fontSize: '13px', color: '#4a4a4a', lineHeight: 1.6 }}>
                                      {o.rationale.strategy_reflection}
                                    </p>
                                  </div>
                                )}
                                {o.rationale.differentiation && (
                                  <div>
                                    <div style={{ fontSize: '11px', color: '#7986cb', marginBottom: '4px', fontWeight: 500 }}>差別化ポイント</div>
                                    <p style={{ margin: 0, fontSize: '13px', color: '#4a4a4a', lineHeight: 1.6 }}>
                                      {o.rationale.differentiation}
                                    </p>
                                  </div>
                                )}
                                {o.rationale.reader_value && (
                                  <div>
                                    <div style={{ fontSize: '11px', color: '#7986cb', marginBottom: '4px', fontWeight: 500 }}>読者価値</div>
                                    <p style={{ margin: 0, fontSize: '13px', color: '#4a4a4a', lineHeight: 1.6 }}>
                                      {o.rationale.reader_value}
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {/* 参考情報リスト */}
                        {researchFindings.length > 0 && (
                          <div
                            style={{
                              background: '#fafafa',
                              borderRadius: '8px',
                              padding: '16px',
                              border: '1px solid #e8e8e8',
                            }}
                          >
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '12px', fontWeight: 600 }}>
                              参考情報リスト ({researchFindings.length}件)
                            </div>
                            <div
                              style={{
                                maxHeight: '455px',
                                overflowY: 'auto',
                              }}
                            >
                              {researchFindings.map((finding, i) => {
                                const refId = i + 1; // 1ベースの参照番号
                                const claimText = finding.claim || finding.finding || finding.content || '';
                                const truncatedClaim = claimText.length > 80 ? claimText.substring(0, 80) + '…' : claimText;
                                const sourceUrl = finding.source_url || '';
                                const isHighlighted = highlightedRefId === refId;
                                
                                return (
                                  <div
                                    key={i}
                                    id={`ref-${idx}-${refId}`}
                                    style={{
                                      padding: '10px 12px',
                                      background: isHighlighted ? '#fffbe6' : '#fff',
                                      borderBottom: '1px solid #f0f0f0',
                                      display: 'flex',
                                      gap: 10,
                                      transition: 'background 0.3s',
                                    }}
                                  >
                                    {/* 参照番号 */}
                                    <div
                                      style={{
                                        minWidth: 24,
                                        height: 24,
                                        borderRadius: 12,
                                        background: isHighlighted ? '#fadb14' : '#f0f0f0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        color: isHighlighted ? '#fff' : '#666',
                                        flexShrink: 0,
                                        transition: 'background 0.3s, color 0.3s',
                                      }}
                                    >
                                      {refId}
                                    </div>
                                    {/* クレーム + ドメイン */}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div
                                        style={{
                                          fontSize: '12px',
                                          lineHeight: 1.5,
                                          color: '#333',
                                          marginBottom: 4,
                                        }}
                                      >
                                        {truncatedClaim}
                                      </div>
                                      {sourceUrl && (
                                        <a
                                          href={sourceUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{
                                            fontSize: '11px',
                                            color: '#888',
                                            textDecoration: 'none',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 4,
                                          }}
                                        >
                                          <span style={{ fontSize: '10px' }}>🔗</span>
                                          <span>{extractDomain(sourceUrl)}</span>
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ),
                };
              })}
            />

            {/* 確定ボタン */}
            <div
              className="outline-actions tour-confirm-button"
              style={{
                marginTop: 20,
                opacity: typewriterRevealed ? 1 : 0,
                transform: typewriterRevealed ? 'translateY(0)' : 'translateY(12px)',
                transition: 'opacity 0.5s ease 0.3s, transform 0.5s ease 0.3s',
              }}
            >
              <Button
                type="primary"
                size="large"
                block
                disabled={selectedIndex === null || isReadOnly}
                onClick={onConfirm}
              >
                この構成案で編集に進む
              </Button>
            </div>
          </div>
        )}
      </Space>
    </div>
  );
};

export default GenerationOutlinePage;
